/**
 * EscalationQueueReport — /#/reports/escalation-queue
 *
 * Per-manager and per-lane breakdown of open SOFT_STOP events with the
 * 72-hour escalation deadline countdown (RULE-ESC-1). Tenant 1 defaults to
 * Demo Manager 2 as the assigned manager — surfaced via tenant_escalation_config.
 */
import { useEffect, useMemo, useState } from "react";
import ReportShell, { thStyle, tdStyle, BLACK, RED, SLATE, BORDER, EmptyState, LoadingState } from "./_ReportShell";
import { EscalationRow, downloadCsv, listEscalationQueue, toCsv } from "@/lib/reports-saturday";

function countdownColor(hours: number): string {
  if (hours < 0) return RED;
  if (hours < 12) return RED;
  if (hours < 24) return "#CA8A04";
  return "#16A34A";
}

function countdownLabel(hours: number): string {
  if (hours < 0) return `Overdue ${Math.abs(hours)}h`;
  if (hours < 24) return `${hours}h left`;
  const d = Math.floor(hours / 24);
  const h = hours % 24;
  return `${d}d ${h}h left`;
}

interface ManagerLaneCounts {
  manager: string;
  lane_key: string;
  cp_number: number;
  open: number;
  overdue: number;
}

export default function EscalationQueueReport() {
  const [rows, setRows] = useState<EscalationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await listEscalationQueue();
        if (!cancelled) setRows(r);
      } catch (err: any) {
        console.error("[EscalationQueueReport]", err);
        if (!cancelled) setError(err?.message ?? "Failed to load");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const perManagerLane = useMemo<ManagerLaneCounts[]>(() => {
    if (!rows) return [];
    const m = new Map<string, ManagerLaneCounts>();
    for (const r of rows) {
      if (r.status !== "PENDING_REVIEW") continue;
      const key = `${r.assigned_manager_name ?? r.assigned_manager_id}__${r.lane_key}`;
      let v = m.get(key);
      if (!v) {
        v = {
          manager: r.assigned_manager_name ?? r.assigned_manager_id,
          lane_key: r.lane_key,
          cp_number: r.cp_number,
          open: 0,
          overdue: 0,
        };
        m.set(key, v);
      }
      v.open += 1;
      if (r.hours_remaining < 0) v.overdue += 1;
    }
    return Array.from(m.values()).sort((a, b) => {
      const am = a.manager.localeCompare(b.manager);
      if (am !== 0) return am;
      return a.lane_key.localeCompare(b.lane_key);
    });
  }, [rows]);

  const csv = useMemo(() => {
    if (!rows) return "";
    return toCsv(rows.map((r) => ({
      account_name: r.account_name ?? "",
      lane_key: r.lane_key,
      cp_number: r.cp_number,
      question_key: r.question_key,
      assigned_manager: r.assigned_manager_name ?? r.assigned_manager_id,
      created_at: r.created_at,
      escalation_deadline: r.escalation_deadline,
      hours_remaining: r.hours_remaining,
      status: r.status,
      decision: r.decision ?? "",
    })));
  }, [rows]);

  return (
    <ReportShell
      title="Escalation Queue"
      subtitle="SOFT_STOP events per manager and per lane with 72-hour deadline countdowns (RULE-ESC-1)."
      rightLabel="Escalation Queue"
      onExport={rows && rows.length > 0 ? () => downloadCsv("escalation-queue.csv", csv) : undefined}
    >
      {error ? (
        <EmptyState message={`Could not load: ${error}`} />
      ) : !rows ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState message="No SOFT_STOP events open. Data activates as reps trigger CP guardrails." />
      ) : (
        <>
          {/* Per-manager × per-lane summary */}
          <div style={{ marginBottom: 24, border: `1px solid ${BORDER}`, borderRadius: 8, background: "#FFFFFF", overflowX: "auto" }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER}`, fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: SLATE }}>
              Open events by manager × lane
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Manager</th>
                  <th style={thStyle}>Lane</th>
                  <th style={thStyle}>CP</th>
                  <th style={thStyle}>Open</th>
                  <th style={thStyle}>Overdue</th>
                </tr>
              </thead>
              <tbody>
                {perManagerLane.map((r) => (
                  <tr key={`${r.manager}-${r.lane_key}`}>
                    <td style={tdStyle}>{r.manager}</td>
                    <td style={tdStyle}>{r.lane_key}</td>
                    <td style={tdStyle}>{r.cp_number}</td>
                    <td style={tdStyle}>{r.open}</td>
                    <td style={{ ...tdStyle, color: r.overdue > 0 ? RED : BLACK, fontWeight: r.overdue > 0 ? 700 : 400 }}>{r.overdue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Detail rows with countdown */}
          <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, background: "#FFFFFF", overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 880 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Account</th>
                  <th style={thStyle}>Lane</th>
                  <th style={thStyle}>CP / Q</th>
                  <th style={thStyle}>Manager</th>
                  <th style={thStyle}>Countdown</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.event_id}>
                    <td style={tdStyle}>{r.account_name ?? "—"}</td>
                    <td style={tdStyle}>{r.lane_key}</td>
                    <td style={tdStyle}>CP{r.cp_number} / {r.question_key}</td>
                    <td style={tdStyle}>{r.assigned_manager_name ?? r.assigned_manager_id}</td>
                    <td style={{ ...tdStyle, color: countdownColor(r.hours_remaining), fontWeight: 700 }}>{countdownLabel(r.hours_remaining)}</td>
                    <td style={tdStyle}>{r.status}{r.decision ? ` · ${r.decision}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </ReportShell>
  );
}
